import { Button, GenericDialog, InputGroup } from '@/common'
import useI18n from '@/shared/hooks/usei18n'
import { useCallback, useState } from 'react'
import { useCreateCbamSimulation } from '@/features/cbam-simulations-v2/hooks/cbamSimulations'
import { useSession } from '@/shared/hooks/useSession'
import toast from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'
import { trackCbamSimulationSignal } from '@/features/cbam-simulations-v2/analytics/cbamSimulationClarity'

export default function CreateCbamSimulationDialog({
  isOpen,
  close
}: {
  isOpen: boolean
  close: () => void
}) {
  const { t } = useI18n()
  const organizationId = useSession().session.organization.id
  const { mutateAsync: createSimulation, isPending: isCreatingSimulation } =
    useCreateCbamSimulation(organizationId)

  const [name, setName] = useState('')

  const navigate = useNavigate()

  const onClose = useCallback(() => {
    setName('')
    close()
  }, [close])

  const onSubmit = useCallback(async () => {
    trackCbamSimulationSignal('simulations:create:submitted')
    try {
      const result = await createSimulation({ name })
      trackCbamSimulationSignal('simulations:create:succeeded')
      toast.success(
        t('cbamSimulations:cbamSimulationsPage.dialogs.createSimulation.notifications.success')
      )
      navigate(`/cbam-simulations/${result.id}`)
      onClose()
    } catch (error) {
      console.error(error)
      trackCbamSimulationSignal('simulations:create:failed')
      toast.error(
        t('cbamSimulations:cbamSimulationsPage.dialogs.createSimulation.notifications.error')
      )
    }
  }, [createSimulation, name, t, onClose, navigate])

  return (
    <GenericDialog
      isOpen={isOpen}
      close={close}
      title={t('cbamSimulations:cbamSimulationsPage.dialogs.createSimulation.title')}
      size="md"
    >
      <div className="space-y-4">
        <form
          onSubmit={evt => {
            evt.preventDefault()
            onSubmit()
          }}
        >
          <div className="space-y-4">
            <InputGroup
              label={t('cbamSimulations:cbamSimulationsPage.dialogs.createSimulation.name.label')}
              placeholder={t(
                'cbamSimulations:cbamSimulationsPage.dialogs.createSimulation.name.placeholder'
              )}
              value={name}
              onChange={evt => setName(evt.target.value)}
            />
          </div>
        </form>
        <footer className="flex justify-end gap-2">
          <Button type="button" variant="neutral" onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button type="submit" disabled={isCreatingSimulation} onClick={onSubmit}>
            {t('common:actions.save')}
          </Button>
        </footer>
      </div>
    </GenericDialog>
  )
}
